import React from "react";
import { Menu, X, User } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { SOCIAL_ICONS } from "@/components/site/socialIcons";
import { LangSwitch, useScrolled } from "../shared/TemplateShell";
import { NavMore, useInlineNavSplit } from "../shared/InlineNav";
import { useSiteContent } from "../shared/useSiteData";

/**
 * Bold / Modern — the theme's CHROME (header + footer), in one place.
 *
 * Extracted from BoldTemplate so the homepage and the interior pages
 * (templates/ThemedPageLayout.jsx) render the SAME two components (BUG-006 / BUG-032).
 *
 * Idiom: brutalist. No radii anywhere, uppercase micro-type, a neon rule under the bar once
 * scrolled; the mobile panel is a full-bleed black slab with a neon top border and a hard
 * offset shadow on the CTA.
 */

export const BOLD_MAIN_CLASS = "min-h-screen bg-[var(--tpl-bg)] text-[var(--tpl-text)]";

/** The theme's monospace accent, used for labels and machine-ish text. */
const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const NEON_BORDER = { borderColor: "var(--tpl-accent)" };

/**
 * The FR/EN/ES switch: the selected language is a FILLED chip, never a colour change alone.
 *
 * It used to be `activeClass="text-black"` — black type on a bar that is transparent over the
 * near-black hero and solid #000 once scrolled, i.e. invisible in both states. Same defect the
 * light themes had with `text-white`, in the other direction. The neon block with black type is
 * this theme's own button treatment (see the Book CTA below).
 */
const LANG_ACTIVE = "bg-[var(--tpl-accent)] text-black";
const LANG_IDLE = "opacity-50 hover:opacity-100";

/**
 * The brand wordmark, SIZED TO ITS OWN LENGTH.
 *
 * This header is `[brand][nav flex-1][actions]`, and the brand carried `shrink-0` — a fixed
 * intrinsic width the row could never take back. With a long practice name (30 characters)
 * that fixed width plus the nav's own non-wrapping link row left no room for the actions
 * zone, and since nothing upstream of it could shrink, the row overflowed and pushed the
 * Book CTA, the language toggle and the account icon off the right edge of the viewport —
 * unreachable, and the header doesn't scroll horizontally (browser-QA
 * 2026-08-19T08-05-52Z-s13c-bold-header-clip, 1280 and 1440). Same defect class as BUG-045
 * (Classic) and the one Luxe just got fixed for.
 *
 * The fix is the same shape as Luxe's, in Bold's own idiom: no `md:` step-up, because this
 * theme never gave the wordmark one — just its one brutalist size, tiered down through
 * shorter faces as the name gets longer, uppercase and tight-tracked at every tier since
 * that's already this theme's most compact setting. `truncate` is the floor so a
 * pathological name ellipsizes on one line instead of ever growing the header or the row.
 * The actions zone keeps its own `shrink-0` — it must never be the side that gives.
 */
function brandClass(brand) {
  const n = (brand || "").length;
  if (n <= 14) return "text-xl";
  if (n <= 22) return "text-lg";
  if (n <= 34) return "text-base";
  return "text-sm";
}

/**
 * How many of the ten nav links this theme can draw inline (BUG-045 — see
 * shared/InlineNav.jsx for why a fixed count and not a breakpoint).
 *
 * The budget: 11px uppercase `tracking-widest` labels with `gap-5` (20px) cost ~90px each,
 * beside the truncating wordmark, the language chips, the 40px account square and the neon
 * CTA. Measured with all ten links, in French, on a 24-character practice name: the bar
 * overflowed by 292px at 1024px and by 21px at 1536px. Removing k links frees ~90k against a
 * "More" pill costing ~60, so 1024px needs k>=4 and 1536px needs k>=1 — 5 and 7 leave a
 * link's worth of margin at both ends.
 */
const INLINE_CAPACITY = { lg: 5, xl: 7 };

export const BoldHeader = ({ toggles = {} }) => {
  const c = useSiteContent(toggles);
  const tt = useT("templates");
  const nav = c.navModel;
  const links = [...nav.sections, ...nav.pages];
  const { inline, overflow } = useInlineNavSplit(nav, INLINE_CAPACITY);
  const [open, setOpen] = React.useState(false);
  const scrolled = useScrolled();

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors ${scrolled ? "border-b" : ""}`}
      style={{ background: scrolled ? "#000" : "transparent", ...NEON_BORDER }}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-6">
        <a
          href={nav.home}
          className={`tpl-head font-bold uppercase tracking-tight min-w-0 truncate ${brandClass(nav.brand)}`}
          data-testid="logo-home"
          title={nav.brand}
        >
          {nav.brand}
        </a>
        <nav className="hidden lg:flex items-center gap-5 uppercase text-[11px] font-semibold tracking-widest flex-1 justify-center">
          {inline.map((l) => (
            <a key={l.key} href={l.href} data-testid={`nav-link-${l.key}`} className="hover:text-[var(--tpl-accent)] transition-colors whitespace-nowrap">{l.label}</a>
          ))}
          <NavMore
            items={overflow}
            className="uppercase text-[11px] font-semibold tracking-widest hover:text-[var(--tpl-accent)] transition-colors"
            menuClassName="bg-black border uppercase text-[11px] font-semibold tracking-widest"
            menuStyle={NEON_BORDER}
            itemClassName="hover:text-[var(--tpl-accent)] transition-colors"
            iconSize={12}
          />
        </nav>
        <div className="flex items-center gap-3 shrink-0">
          <LangSwitch lang={c.lang} setLang={c.setLang} className="hidden sm:flex uppercase" activeClass={LANG_ACTIVE} idleClass={LANG_IDLE} />
          {nav.account && (
            <a href={nav.account.href} data-testid="nav-client-portal" aria-label={nav.account.label} title={nav.account.label} className="hidden sm:inline-flex items-center justify-center w-10 h-10 border border-white/20 hover:border-[var(--tpl-accent)] hover:text-[var(--tpl-accent)] transition-colors">
              <User size={16} />
            </a>
          )}
          {nav.book && (
            <a href={nav.book.href} data-testid="nav-book-cta" className="hidden sm:inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-bold uppercase text-black hover:bg-white hover:shadow-[4px_4px_0_#CCFF00] transition-all whitespace-nowrap" style={{ background: "var(--tpl-accent)" }}>{nav.book.label}</a>
          )}
          <button
            className="lg:hidden"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label={open ? tt("shared.nav.closeMenu") : tt("shared.nav.openMenu")}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-black border-t px-6 py-6" style={NEON_BORDER} data-testid="mobile-menu">
          <div className="flex flex-col gap-4 uppercase text-sm font-semibold tracking-widest">
            {links.map((l) => (
              <a key={l.key} href={l.href} onClick={() => setOpen(false)}>{l.label}</a>
            ))}
            {nav.account && (
              <a href={nav.account.href} onClick={() => setOpen(false)} className="inline-flex items-center gap-2">
                <User size={16} /> {nav.account.label}
              </a>
            )}
          </div>
          <div className="mt-6 pt-5 border-t border-white/15 flex items-center justify-between gap-4">
            <LangSwitch lang={c.lang} setLang={c.setLang} className="uppercase" activeClass={LANG_ACTIVE} idleClass={LANG_IDLE} />
            {nav.book && (
              <a href={nav.book.href} onClick={() => setOpen(false)} className="px-5 py-3 text-sm font-bold uppercase text-black" style={{ background: "var(--tpl-accent)" }}>{nav.book.label}</a>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

/**
 * The marquee WAS the whole footer (BUG-026): a visitor who scrolled to the bottom found a
 * decorative ticker and no link at all. It stays, as decoration, ABOVE a functional
 * neon-hairline sitemap — and outside the `<footer>` element, hidden from assistive tech.
 */
export const BoldFooter = ({ toggles = {} }) => {
  const f = useSiteContent(toggles).footerModel;

  return (
    <>
      <div className="overflow-hidden py-6 whitespace-nowrap border-t" style={{ background: "var(--tpl-accent)", ...NEON_BORDER }} aria-hidden="true">
        <div className="marquee-track inline-flex gap-10 text-black tpl-head text-3xl font-bold uppercase">
          {Array(8).fill(`${f.brand} — © ${f.year}`).map((w, i) => <span key={i} className="inline-flex items-center gap-10">{w} <span>✦</span></span>)}
        </div>
      </div>

      <footer className="px-6 md:px-10 py-16" style={{ background: "#000" }} data-testid="site-footer">
        <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-px" style={{ background: "rgba(255,255,255,0.15)" }}>
          <div className="p-8" style={{ background: "#000" }}>
            <div className="tpl-head text-2xl font-bold uppercase" style={{ color: "var(--tpl-accent)" }}>{f.brand}</div>
            {/* BUG-210: no dictionary sample any more — an unset tagline is a fact the
                practice hasn't stated, not filler text. */}
            {f.tagline && <p className="mt-3 text-sm text-white/50 leading-relaxed">{f.tagline}</p>}
            {f.socials.length > 0 && (
              <div className="mt-6 flex flex-wrap items-center gap-2">
                {f.socials.map((s) => {
                  const Icon = SOCIAL_ICONS[s.key];
                  return (
                    <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.label} data-testid={`footer-social-${s.key}`} className="w-10 h-10 border border-white/20 flex items-center justify-center hover:bg-[var(--tpl-accent)] hover:text-black transition-colors">
                      <Icon size={16} />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          <div className="p-8" style={{ background: "#000" }}>
            <div style={{ ...MONO, color: "var(--tpl-accent)" }} className="text-xs uppercase">// {f.headings.navigate}</div>
            <div className="mt-4 space-y-2 uppercase text-xs font-semibold tracking-widest">
              {f.sections.map((s) => (
                <a key={s.key} href={s.href} data-testid={`footer-section-${s.key}`} className="block text-white/70 hover:text-[var(--tpl-accent)] transition-colors">{s.label}</a>
              ))}
            </div>
          </div>

          <div className="p-8" style={{ background: "#000" }}>
            <div style={{ ...MONO, color: "var(--tpl-accent)" }} className="text-xs uppercase">// {f.headings.explore}</div>
            <div className="mt-4 space-y-2 uppercase text-xs font-semibold tracking-widest">
              {f.pages.map((p) => (
                <a key={p.key} href={p.href} data-testid={`footer-page-${p.key}`} className="block text-white/70 hover:text-[var(--tpl-accent)] transition-colors">{p.label}</a>
              ))}
            </div>
          </div>

          <div className="p-8" style={{ background: "#000" }}>
            <div style={{ ...MONO, color: "var(--tpl-accent)" }} className="text-xs uppercase">// {f.headings.contact}</div>
            <div className="mt-4 space-y-2 text-sm text-white/70" style={MONO}>
              <a href={`mailto:${f.contact.email}`} className="block break-all hover:text-[var(--tpl-accent)]">{f.contact.email}</a>
              <div>{f.contact.place}</div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto mt-10 text-xs uppercase tracking-widest text-white/40" style={MONO} data-testid="footer-copyright">
          © {f.year} {f.brand} — {f.rights}
        </div>
      </footer>
    </>
  );
};

/** The chrome contract templates/registry.js registers for t4_bold_modern. */
export const BOLD_CHROME = {
  Header: BoldHeader,
  Footer: BoldFooter,
  mainClass: BOLD_MAIN_CLASS,
};

export default BOLD_CHROME;
